/**
 * @spec /doc/specs/frontend/design-system/mockups/index.html
 * @userStory S2-LANDING, US-EP022-008
 *
 * Internationalization routing configuration using next-intl.
 * Supports EN (default) and FR locales.
 *
 * NOTE on FR-translated marketing slugs (e.g. /plateforme vs /platform):
 * Adding `pathnames` to defineRouting() switches Link/redirect to a strict
 * literal-union type for `href`, which would break ~50 existing dashboard
 * call-sites (`/login`, `/signup`, `/dashboard`, `/c/${id}/sov`, `/lifts/${id}`,
 * `/studios/${id}`, etc.). For S39 we keep the simple routing (URLs are
 * `/fr/platform`, `/fr/methodology`, …) and rely on canonical + hreflang
 * (set via `generateMarketingMetadata`) for SEO. FR-translated marketing slugs
 * can be re-introduced in a follow-up once dashboard Links are migrated off
 * `@/i18n/navigation` for paths not in `pathnames`.
 */

import { defineRouting } from 'next-intl/routing';

export const locales = ['en', 'fr'] as const;
export type Locale = (typeof locales)[number];

export const routing = defineRouting({
  locales,
  defaultLocale: 'en',
  localePrefix: 'as-needed',
});

/**
 * Marketing pathnames — kept as a separate constant so:
 *   1. `generateMarketingMetadata` can build hreflang URLs from these aliases.
 *   2. The `sitemap.ts` route emits both EN and FR URLs.
 *   3. Future migration to `routing.pathnames` (after dashboard Link cleanup)
 *      has a single source of truth to consume.
 *
 * The actual `<Link href="...">` always uses the EN slug; FR users see the
 * EN URL prefixed by `/fr` (e.g. `/fr/platform`). Dynamic FR aliases will
 * land via Next.js rewrites + middleware in a follow-up US.
 */
export const marketingPathnames = {
  '/': { en: '/', fr: '/' },
  '/platform': { en: '/platform', fr: '/platform' },
  '/methodology': { en: '/methodology', fr: '/methodology' },
  '/agents': { en: '/agents', fr: '/agents' },
  '/case-studies': { en: '/case-studies', fr: '/case-studies' },
  '/case-studies/[slug]': { en: '/case-studies/[slug]', fr: '/case-studies/[slug]' },
  '/offer': { en: '/offer', fr: '/offer' },
  '/poc': { en: '/poc', fr: '/poc' },
  '/resources': { en: '/resources', fr: '/resources' },
  '/resources/[slug]': { en: '/resources/[slug]', fr: '/resources/[slug]' },
  '/about': { en: '/about', fr: '/about' },
  '/contact': { en: '/contact', fr: '/contact' },
  '/aeo': { en: '/aeo', fr: '/aeo' },
  '/geo': { en: '/geo', fr: '/geo' },
  '/share-of-voice': { en: '/share-of-voice', fr: '/share-of-voice' },
  // EP033 — new page families (hubs + dynamic [slug]). Slug is EN-canonical,
  // FR served at /fr/<en-slug> (no FR-translated slugs — see note above).
  '/agentic-commerce': { en: '/agentic-commerce', fr: '/agentic-commerce' },
  '/agentic-commerce/[slug]': {
    en: '/agentic-commerce/[slug]',
    fr: '/agentic-commerce/[slug]',
  },
  '/answers': { en: '/answers', fr: '/answers' },
  '/answers/[slug]': { en: '/answers/[slug]', fr: '/answers/[slug]' },
  '/compare': { en: '/compare', fr: '/compare' },
  '/compare/[slug]': { en: '/compare/[slug]', fr: '/compare/[slug]' },
  '/glossary': { en: '/glossary', fr: '/glossary' },
  '/glossary/[slug]': { en: '/glossary/[slug]', fr: '/glossary/[slug]' },
  '/solutions': { en: '/solutions', fr: '/solutions' },
  '/solutions/[slug]': { en: '/solutions/[slug]', fr: '/solutions/[slug]' },
  '/pilot': { en: '/pilot', fr: '/pilot' },
  '/legal/privacy': { en: '/legal/privacy', fr: '/legal/privacy' },
  '/legal/terms': { en: '/legal/terms', fr: '/legal/terms' },
  '/legal/cookies': { en: '/legal/cookies', fr: '/legal/cookies' },
  '/legal/notice': { en: '/legal/notice', fr: '/legal/notice' },
} as const;

export type MarketingPathKey = keyof typeof marketingPathnames;
